"use client";

// Shared primitives for the integrated documentation view.
// Styling follows the app's shadcn tokens (light + dark) so the docs adapt
// to the active theme instead of shipping their own palette.

import { useState, type ReactNode } from "react";

export type DocSection = {
  id: string;
  num: string;
  part: 1 | 2 | 3;
  title: string;
  body: ReactNode;
};

const badgeTones = {
  neutral:
    "border-border bg-secondary text-secondary-foreground",
  dark: "border-transparent bg-primary text-primary-foreground",
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
  violet:
    "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300",
  sky: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300",
  amber:
    "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300",
  rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300",
} as const;

export type BadgeTone = keyof typeof badgeTones;

export function DocBadge({
  tone = "neutral",
  children,
}: {
  tone?: BadgeTone;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${badgeTones[tone]}`}
    >
      {children}
    </span>
  );
}

export function Callout({
  tone = "note",
  label,
  children,
}: {
  tone?: "note" | "warn" | "info";
  label: string;
  children: ReactNode;
}) {
  const bar =
    tone === "warn"
      ? "border-l-amber-500"
      : tone === "info"
        ? "border-l-sky-500"
        : "border-l-emerald-600";
  const labelCls =
    tone === "warn"
      ? "text-amber-600 dark:text-amber-400"
      : tone === "info"
        ? "text-sky-600 dark:text-sky-400"
        : "text-emerald-600 dark:text-emerald-400";
  return (
    <div className={`my-5 rounded-lg border bg-card p-4 text-sm ${bar} border-l-4`}>
      <div className={`mb-1 text-[11px] font-semibold uppercase tracking-wider ${labelCls}`}>
        {label}
      </div>
      <div className="space-y-2 text-muted-foreground">{children}</div>
    </div>
  );
}

export function CodeBlock({
  children,
  copyLabel = "Copy",
  copiedLabel = "Copied",
}: {
  children: string;
  copyLabel?: string;
  copiedLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <pre className="relative my-4 overflow-x-auto rounded-lg border bg-muted/40 p-4 pe-16 font-mono text-[13px] leading-relaxed">
      <code>{children}</code>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard
            .writeText(children)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1200);
            })
            .catch(() => {});
        }}
        className="absolute end-2 top-2 rounded-md border bg-background px-2 py-0.5 text-[11px] text-muted-foreground hover:text-primary"
      >
        {copied ? copiedLabel : copyLabel}
      </button>
    </pre>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="my-3 max-w-[68ch] leading-relaxed">{children}</p>;
}

export function L({ children }: { children: ReactNode }) {
  return (
    <ul className="my-3 max-w-[68ch] list-disc space-y-1.5 ps-6 leading-relaxed">
      {children}
    </ul>
  );
}

export function OL({ children }: { children: ReactNode }) {
  return (
    <ol className="my-3 max-w-[68ch] list-decimal space-y-1.5 ps-6 leading-relaxed">
      {children}
    </ol>
  );
}

export function H3({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 mt-6 font-semibold">{children}</h3>;
}

export function DocTable({
  head,
  rows,
}: {
  head: ReactNode[];
  rows: ReactNode[][];
}) {
  return (
    <div className="my-4 max-w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className="border-b px-3 py-2 text-start text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-muted/40">
              {r.map((c, j) => (
                <td key={j} className="border-b px-3 py-2 align-top">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
